/* eslint-disable @typescript-eslint/ban-ts-comment */
import Input from "../components/Shared/Input";
import Button from "../components/Shared/Button";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { ButtonTypes } from "../utils/consts";
import { queryClient } from "../main";
import { toast } from "react-toastify";
import { ChangeUserPassword } from "../api/Auth/changepassword";
import { useUserDataStore } from "../stores/user";
import { Helmet } from "react-helmet";
import { EditStudentData } from "../api/Students/UpdateUserData";
import { useState } from "react";
import { DateRange } from "../components/Auth/RegisterForm";
import Datepicker from "react-tailwindcss-datepicker";
import { countries } from "../utils/consts";
import CountriesSelectbox, {
  option,
} from "../components/Shared/CountriesSelectbox";
import { useSearchParams } from "react-router-dom";

const validationSchema = Yup.object({
  currentPassword: Yup.string().required("Current Password is required"),
  newPassword: Yup.string()
    .required("Password is required")
    .matches(
      /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/,
      "Must Contain 8 Characters, One Uppercase, One Lowercase , One Special Character and One Number"
    ),
  confirmNewPassword: Yup.string()
    .required("Confirm password is required")
    .oneOf([Yup.ref("newPassword"), ""], "Passwords must match"),
});

const AccountSettings = () => {
  const { userData, setUserData } = useUserDataStore();
  const [activeTab, setActiveTab] = useState("info");
  const [selected, setSelected] = useState<option | null>(() => {
    const userCountry = userData?.country;
    if (userCountry) {
      const matchedCountry = countries.find(
        (country) => country.name === userCountry
      );
      return matchedCountry
        ? { name: matchedCountry.name, code: matchedCountry.code }
        : null;
    }
    return null;
  });
  const [value, setValue] = useState<DateRange>(() => {
    const birthDate = userData?.birthDate;
    return {
      startDate: birthDate ? new Date(birthDate) : null,
      endDate: birthDate ? new Date(birthDate) : null,
    };
  });
  const handleValueChange = (newValue: DateRange) => {
    setValue(newValue);
    formikUserData.setFieldValue(
      "birthDate",
      newValue.startDate ? newValue.startDate.toISOString().split("T")[0] : ""
    );
  };

  const { mutate, error } = useMutation({
    mutationFn: ChangeUserPassword,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user"] });
      toast.success("Password Changed successfully");
      formik.resetForm();
    },
    onError: () => {},
  });
  const studentId = userData?.id || "";
  const formik = useFormik({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmNewPassword: "",
    },
    validationSchema,
    onSubmit: (values) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { confirmNewPassword, ...payload } = values;
      //@ts-ignore
      mutate(payload);
    },
  });
  const { mutate: mutateUserData } = useMutation({
    mutationFn: EditStudentData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["userprofile"] });
      toast.success("User data updated successfully");
      //@ts-ignore
      setUserData(data.data);
      formik.setSubmitting(true);
    },
    onError: () => {
      formik.setSubmitting(false);
    },
  });

  const formikUserData = useFormik({
    initialValues: {
      firstName: userData?.firstName ?? "",
      lastName: userData?.lastName ?? "",
      email: userData?.email ?? "",
      phone: userData?.phone ?? "",
      country: userData?.country ?? "",
      jobTitle: userData?.jobTitle ?? "",
      birthDate: userData?.birthDate ? userData?.birthDate.split("T")[0] : "",
    },
    onSubmit: (values) => {
      if (studentId) {
        //@ts-ignore
        mutateUserData({ id: studentId, payload: values });
      }
    },
  });
  const handleCountrySelect = (option: option | null) => {
    setSelected(option);
    formikUserData.setFieldValue("country", option ? option.name : "");
  };

  const [searchParams] = useSearchParams();
  const isContinueRegistration =
    searchParams.get("continue-registration") === "true";

  const renderTabContent = () => {
    if (activeTab === "info") {
      return (
        <div className="lg:p-8 p-4 rounded-xl bg-white border border-black/15 lg:pt-20 pt-12 pb-12">
          <form
            onSubmit={formikUserData.handleSubmit}
            className="flex md:flex-row flex-col md:items-start items-center lg:gap-12 gap-8"
          >
            <div className="relative size-[132px] rounded-full object-cover">
              <img
                className="rounded-full size-[110px] object-cover"
                src={`${userData?.img_url}`}
              />
            </div>
            <div className="w-full space-y-5">
              <div className="flex sm:flex-row flex-col sm:gap-7 gap-5 w-full">
                <Input
                  value={formikUserData.values.firstName}
                  onChange={formikUserData.handleChange}
                  onBlur={formikUserData.handleBlur}
                  className="w-full"
                  title="First Name"
                  inputStyles=" !h-[50px] !rounded-2xl !border-[#DFEAF2] hover:!border-primary"
                  name="firstName"
                  placeholder=""
                />
                <Input
                  value={formikUserData.values.lastName}
                  onChange={formikUserData.handleChange}
                  onBlur={formikUserData.handleBlur}
                  className="w-full"
                  title="Last Name"
                  inputStyles=" !h-[50px] !rounded-2xl !border-[#DFEAF2] hover:!border-primary"
                  name="lastName"
                  placeholder=""
                />
              </div>
              <Input
                value={formikUserData.values.email}
                onChange={formikUserData.handleChange}
                onBlur={formikUserData.handleBlur}
                className="w-full"
                title="Email"
                inputStyles=" !h-[50px] !rounded-2xl !border-[#DFEAF2] hover:!border-primary"
                name="email"
                placeholder=""
              />
              <Input
                value={formikUserData.values.phone}
                onChange={formikUserData.handleChange}
                onBlur={formikUserData.handleBlur}
                className="w-full"
                title="Phone Number"
                inputStyles=" !h-[50px] !rounded-2xl !border-[#DFEAF2] hover:!border-primary"
                name="phone"
                placeholder=""
              />
              <div className="flex sm:flex-row flex-col sm:gap-7 gap-5 w-full">
                <Input
                  value={formikUserData.values.jobTitle}
                  onChange={formikUserData.handleChange}
                  onBlur={formikUserData.handleBlur}
                  className="w-full"
                  title="Job Title"
                  inputStyles=" !h-[50px] !rounded-2xl !border-[#DFEAF2] hover:!border-primary"
                  name="jobTitle"
                  placeholder=""
                />
                <div
                  className={`md:space-y-2 space-y-1.5 relative font-medium w-full`}
                >
                  <label className="text-burntBlack md:text-base text-sm">
                    Birthdate
                  </label>
                  <Datepicker
                    popoverDirection="down"
                    containerClassName="z-[999] relative"
                    placeholder=" "
                    toggleClassName="absolute top-1/2 -translate-y-1/2 right-4"
                    inputClassName="!h-[50px] !rounded-2xl !border-[#DFEAF2] hover:!border-primary border block w-full p-4 focus:outline-none hover:border-primary hover:border-opacity-100 transition-all ease-in focus:border-secondary focus:ring-secondary/50 rounded-lg sm:text-base text-sm"
                    asSingle={true}
                    showShortcuts={true}
                    showFooter={true}
                    value={value}
                    //@ts-ignore
                    onChange={handleValueChange}
                  />
                </div>
              </div>
              <CountriesSelectbox
                title="Country"
                titleStyles="!text-base !mb-2"
                className="!h-[50px] !rounded-2xl !border-[#DFEAF2] hover:!border-primary"
                name="country"
                selectOptions={countries}
                setSelected={handleCountrySelect}
                selected={selected}
              />

              <Button
                className="!h-12 w-44 !text-sm font-bold rounded-md ml-auto !bg-[#4379EE] hover:!bg-primary"
                variant="primary"
                type={ButtonTypes.SUBMIT}
              >
                Update Info
              </Button>
              {error instanceof AxiosError && (
                <span className="text-red-500 font-bold text-lg">
                  {typeof error?.response?.data?.message === "string"
                    ? error?.response?.data?.message
                    : error?.message
                    ? error.message
                    : error?.response?.data?.message
                    ? JSON.stringify(error?.response?.data?.message)
                    : "An error occurred"}
                </span>
              )}
            </div>
          </form>
        </div>
      );
    }

    if (activeTab === "password") {
      return (
        <div className="lg:p-8 p-4 rounded-xl bg-white border border-black/15 lg:pt-20 pt-12 pb-12">
          <form
            onSubmit={formik.handleSubmit}
            className="flex md:flex-row flex-col md:items-start items-center lg:gap-12 gap-8"
          >
            <div className="relative size-[132px] rounded-full object-cover">
              <img
                className="rounded-full size-[110px] object-cover"
                src={`${userData?.img_url}`}
              />
            </div>
            <div className="w-full space-y-5">
              <div className="flex gap-7 w-full">
                <Input
                  value={formik.values.currentPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.currentPassword &&
                    formik.errors.currentPassword
                      ? formik.errors.currentPassword
                      : ""
                  }
                  className="w-full"
                  title="Current Password"
                  inputStyles=" !h-[50px] !rounded-2xl !border-[#DFEAF2] hover:!border-primary"
                  name="currentPassword"
                  type="password"
                  placeholder=""
                />
              </div>
              <Input
                value={formik.values.newPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.newPassword && formik.errors.newPassword
                    ? formik.errors.newPassword
                    : ""
                }
                className="w-full"
                title="New Password"
                inputStyles=" !h-[50px] !rounded-2xl !border-[#DFEAF2] hover:!border-primary"
                name="newPassword"
                type="password"
                placeholder=""
              />
              <Input
                value={formik.values.confirmNewPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.confirmNewPassword &&
                  formik.errors.confirmNewPassword
                    ? formik.errors.confirmNewPassword
                    : ""
                }
                className="w-full"
                title="Confirm New Password"
                inputStyles=" !h-[50px] !rounded-2xl !border-[#DFEAF2] hover:!border-primary"
                name="confirmNewPassword"
                type="password"
                placeholder=""
              />

              <Button
                className="!h-12 w-44 !text-sm font-bold rounded-md ml-auto !bg-[#4379EE] hover:!bg-primary"
                variant="primary"
                type={ButtonTypes.SUBMIT}
              >
                Change Password
              </Button>
              {error instanceof AxiosError && (
                <span className="text-red-500 font-bold text-lg">
                  {typeof error?.response?.data?.message === "string"
                    ? error?.response?.data?.message
                    : error?.message
                    ? error.message
                    : error?.response?.data?.message
                    ? JSON.stringify(error?.response?.data?.message)
                    : "An error occurred"}
                </span>
              )}
            </div>
          </form>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="py-7 container max-w-[1100px] w-full mx-auto sm:my-28 mb-28 mt-20">
      <Helmet>
        <link rel="canonical" href="https://www.nilewisect.com/settings" />
      </Helmet>

      {isContinueRegistration && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-yellow-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                Please complete your profile information to proceed. You won't
                be able to access other pages until you provide all required
                information.
              </p>
            </div>
          </div>
        </div>
      )}

      <h1 className="text-burntBlack font-bold sm:text-[32px] text-2xl mb-7">
        Account Settings
      </h1>
      <div className="flex border-b border-gray-200 mb-7">
        <button
          className={`sm:px-5 px-3 py-3 transition-all duration-300 font-medium border-b-2 border-transparent ${
            activeTab === "info"
              ? " !border-secondary text-secondary"
              : "hover:text-primary  hover:border-primary"
          }`}
          onClick={() => setActiveTab("info")}
        >
          User Information
        </button>
        <button
          className={`sm:px-5 px-3 py-3 transition-all duration-300 font-medium border-b-2 border-transparent ${
            activeTab === "password"
              ? " !border-secondary text-secondary"
              : "hover:text-primary hover:border-primary"
          }`}
          onClick={() => setActiveTab("password")}
        >
          Change Password
        </button>
      </div>
      {renderTabContent()}
    </div>
  );
};

export default AccountSettings;
